import React from 'react';
import Lock from 'lucide-react/dist/esm/icons/lock.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import { squareStatusClass } from '../config/squarePalette';
import SquareLegend from './SquareLegend';

/**
 * SquareBoardPreview
 * 
 * A presentational component for rendering square game boards in layout previews.
 * 
 * **Data Resolution Strategy:**
 * 1. If `squares` array is provided and non-empty: Uses real per-cell status data
 * 2. Otherwise: Falls back to proportional visual estimate using `ticketsSold / totalSquares`
 *    (fills first N cells row-major as "taken" for decorative preview)
 * 
 * **Number Display:**
 * - Shows cell numbers when `totalSquares <= 100` (readable)
 * - Hides numbers for larger boards (>100) to maintain cell clarity
 * 
 * @component
 * @param {Object} props
 * @param {number} [props.totalSquares=100] - Grid size (e.g., 100 for 10×10)
 * @param {Array<{number: number, status: 'available'|'taken'|'sold'|'held'|'reserved'}>} [props.squares] - Real square data
 * @param {number} [props.ticketsSold] - Used for proportional fallback when squares is empty
 * @param {number|null} [props.winningSquare] - Square number to highlight as winner
 * @param {(number) => void} [props.onSquareClick] - Click handler (omit for read-only)
 * @param {boolean} [props.loading] - Show skeleton loading state
 * @param {string} [props.heading] - Optional heading text above grid
 * @param {'sm'|'md'} [props.size='sm'] - Size variant (sm=compact, md=larger)
 */
export default function SquareBoardPreview({
  totalSquares = 100,
  squares,
  ticketsSold,
  winningSquare = null,
  onSquareClick,
  loading = false,
  heading,
  size = 'sm',
}) {
  const cols = Math.ceil(Math.sqrt(totalSquares));
  const rows = Math.ceil(totalSquares / cols);
  const showNumbers = totalSquares <= 100;

  // Determine which cells are filled
  let cellStates;
  if (squares && squares.length > 0) {
    // Real data mode: build lookup map
    const squareMap = {};
    squares.forEach((sq) => {
      squareMap[sq.number] = sq.status;
    });
    cellStates = (num) => squareMap[num] || 'available';
  } else {
    // Proportional fallback mode
    const filledCount = ticketsSold
      ? Math.max(0, Math.min(totalSquares, Math.round((ticketsSold / totalSquares) * totalSquares)))
      : 0;
    cellStates = (num) => (num <= filledCount ? 'taken' : 'available');
  }

  // Scale text based on grid size and size variant
  // ≤100: large numbers fit well
  // 101-400: smaller to prevent overflow
  // >400: minimal (though showNumbers hides for >100 anyway)
  let textClass;
  if (totalSquares <= 100) {
    textClass = size === 'sm' ? 'text-lg sm:text-xl md:text-2xl' : 'text-xl sm:text-2xl md:text-3xl';
  } else if (totalSquares <= 400) {
    textClass = size === 'sm' ? 'text-base sm:text-lg md:text-xl' : 'text-lg sm:text-xl md:text-2xl';
  } else {
    textClass = size === 'sm' ? 'text-xs sm:text-sm md:text-base' : 'text-sm sm:text-base md:text-lg';
  }

  return (
    <div className="w-full">
      {heading && (
        <h3 className="text-xl font-bold text-white mb-4">{heading}</h3>
      )}

      {/* Legend — shown above the grid */}
      <SquareLegend showWinner={winningSquare !== null} />

      <div
        className="grid gap-1.5 w-full mt-3"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: totalSquares }, (_, i) => {
          const num = i + 1;
          const status = cellStates(num);
          const isTaken = status === 'taken' || status === 'sold';
          const isHeld = status === 'held' || status === 'reserved';
          const isWinning = winningSquare === num;
          const isAvailable = !isTaken && !isHeld && !isWinning;
          const isInteractive = onSquareClick && isAvailable;

          // Base classes for all cells
          let cellClass = `aspect-square w-full rounded transition-all duration-150 flex items-center justify-center relative border-2 ${textClass} font-semibold `;

          if (loading) {
            cellClass += 'animate-pulse bg-gray-700/50 ';
          } else {
            // Shared palette drives the per-status colors.
            const paletteKey = isWinning
              ? 'winner'
              : isTaken
              ? (status === 'sold' ? 'sold' : 'taken')
              : isHeld
              ? 'held'
              : 'available';
            const palette = squareStatusClass(paletteKey);
            cellClass += `${palette.cell} `;
            if (isInteractive) {
              cellClass += `${palette.hover} cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 `;
            } else if (isHeld || isTaken) {
              cellClass += 'cursor-not-allowed ';
            }
          }

          const ariaLabel = isWinning
            ? `Square ${num}, winning square`
            : isTaken
            ? `Square ${num}, taken`
            : isHeld
            ? `Square ${num}, reserved`
            : `Square ${num}, available`;

          const CellTag = isInteractive ? 'button' : 'div';
          const cellProps = {
            key: num,
            className: cellClass,
            'aria-label': ariaLabel,
          };

          if (isInteractive) {
            cellProps.type = 'button';
            cellProps.onClick = () => onSquareClick(num);
          } else if (isTaken || isHeld) {
            cellProps.disabled = true;
            cellProps['aria-disabled'] = 'true';
          }

          return (
            <CellTag {...cellProps}>
              {/* Number display (only for small boards) */}
              {!loading && showNumbers && (
                <span className={`${isTaken ? 'opacity-60' : isHeld ? 'opacity-80' : 'opacity-100'} z-10`}>
                  {num}
                </span>
              )}

              {/* Icons */}
              {!loading && (
                <>
                  {/* Winning trophy */}
                  {isWinning && (
                    <Trophy
                      className="absolute inset-0 m-auto text-white drop-shadow-md"
                      size={size === 'sm' ? 14 : 18}
                      strokeWidth={2.5}
                    />
                  )}

                  {/* Taken lock icon */}
                  {isTaken && !isWinning && (
                    <Lock
                      className="absolute inset-0 m-auto text-gray-500 dark:text-gray-600"
                      size={size === 'sm' ? 10 : 12}
                      strokeWidth={2}
                    />
                  )}

                  {/* Held clock icon (bottom-right corner so the number stays legible) */}
                  {isHeld && !isWinning && (
                    <Clock
                      className="absolute bottom-0 right-0 text-amber-400 dark:text-amber-300 drop-shadow"
                      size={size === 'sm' ? 8 : 10}
                      strokeWidth={2.5}
                    />
                  )}
                </>
              )}
            </CellTag>
          );
        })}
      </div>
    </div>
  );
}
